/**
 * Referral Reports — R2 §3.5
 *
 * Report builder for the Reporting R1 backend. All controls (fields,
 * measures, grains, operators) are catalog-driven — the /reports/fields
 * response is the single source of truth. No hardcoded field lists live
 * here (grep pin: field names appear only via /fields consumption).
 *
 * Route: /referral-reports (admin + sales). The pre-existing /reports
 * route is the ActivityReports (staff-activity) page — left untouched
 * per this ticket's hard constraints.
 *
 * Backend contract shape:
 *   • date_base + date_grain (backend names; brief called them
 *     date_basis + grain — reconciled to the actual API here).
 *   • measures: [{ id, ...args }]   (backend uses `id`, not `kind`).
 *   • sort:     [{ field, dir }]    (backend uses `field`, not `by`).
 *   • filters:  [{ field, op, value }]
 *   • grains:   ["none","day","week","month","ytd"]
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../api/client";
import {
  bucketKeysForRange, buildChartSeries, effectiveGrain, grandTotal,
  intakeRowsNote, isGroupedResult, resultCountLabel, spansYears,
} from "../lib/reportChart";
import { ReportTimeChart } from "../components/ReportTimeChart";
import {
  engineToPickerTo, inclusiveRange, pickerToEngineTo, rangeEcho,
  rangeForPreset, rangeValidationError,
} from "../lib/reportRange";

// Preset date ranges. The ENGINE's range is end-exclusive
// (date_from <= x < date_to) and stays that way; the UI is inclusive and
// `lib/reportRange` is the single translation layer (see its header).
const DATE_PRESETS = [
  { id: "last7",  label: "Last 7 days",  days: 7 },
  { id: "last30", label: "Last 30 days", days: 30 },
  { id: "last90", label: "Last 90 days", days: 90 },
  { id: "mtd",   label: "Month to date" },
  { id: "ytd",   label: "Year to date" },
  { id: "custom", label: "Custom range" },
];

// Returns the ENGINE range. For custom ranges the inclusive picker "To"
// is translated (+1 day) here — the one place it happens on submit.
const rangeFor = (preset, from, to) => {
  if (preset === "custom") {
    return { date_from: from || null, date_to: pickerToEngineTo(to) };
  }
  const found = DATE_PRESETS.find((p) => p.id === preset);
  return rangeForPreset(preset, found?.days || 30);
};

// Default operator per catalog field.type. The backend accepts a fixed
// operator set; we surface a curated subset per field type here.
const OPS_FOR_TYPE = {
  string:   ["eq", "ne", "in", "not_in", "like", "not_like", "is_null", "is_not_null"],
  int:      ["eq", "ne", "gt", "gte", "lt", "lte", "between", "in", "not_in", "is_null", "is_not_null"],
  float:    ["eq", "ne", "gt", "gte", "lt", "lte", "between", "is_null", "is_not_null"],
  boolean:  ["eq", "ne", "is_null", "is_not_null"],
  date:     ["eq", "ne", "gt", "gte", "lt", "lte", "between", "is_null", "is_not_null"],
  datetime: ["eq", "ne", "gt", "gte", "lt", "lte", "between", "is_null", "is_not_null"],
};

// Fields whose values render with the Incomplete-caveat chip. Any status-
// group surface (filter picker + results) shows the tooltip.
const INCOMPLETE_TIP = "Incomplete referrals arrive with R3";

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── Filter chip ────────────────────────────────────────────────────────
function FilterChip({ field, filter, onChange, onRemove }) {
  const ops = OPS_FOR_TYPE[field.type] || OPS_FOR_TYPE.string;
  const takesList = filter.op === "in" || filter.op === "not_in" || filter.op === "between";
  const takesValue = !["is_null", "is_not_null"].includes(filter.op);
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-sm"
      data-testid={`filter-chip-${field.id}`}
    >
      <span className="font-medium text-slate-700">{field.label}</span>
      <select
        className="rounded border border-slate-300 bg-white px-1 text-xs"
        value={filter.op}
        onChange={(e) => onChange({ ...filter, op: e.target.value })}
        data-testid={`filter-op-${field.id}`}
      >
        {ops.map((op) => <option key={op} value={op}>{op}</option>)}
      </select>
      {takesValue && (
        <input
          className="w-32 rounded border border-slate-300 px-2 text-xs"
          placeholder={takesList ? "a,b,c" : "value"}
          value={
            Array.isArray(filter.value)
              ? filter.value.join(",")
              : (filter.value ?? "")
          }
          onChange={(e) => {
            const raw = e.target.value;
            const value = takesList ? raw.split(",").map((s) => s.trim()).filter(Boolean) : raw;
            onChange({ ...filter, value });
          }}
          data-testid={`filter-value-${field.id}`}
        />
      )}
      <button
        className="text-slate-500 hover:text-red-600"
        onClick={onRemove}
        data-testid={`filter-remove-${field.id}`}
      >
        ×
      </button>
    </div>
  );
}

// ── Measure spec editor ────────────────────────────────────────────────
function MeasureChip({ measure, catalog, onChange, onRemove, measures }) {
  const spec = measures.find((m) => m.id === measure.id);
  const argFields = (kind) => {
    // count_distinct.field: any catalog field. avg_days/median_days
    // .from/.to and pct_reached.milestone: catalog fields flagged as
    // timestamps (is_date_or_ts).
    if (kind === "count_distinct") return catalog.filter((f) => f.filterable !== false);
    return catalog.filter((f) => f.is_date_or_ts);
  };
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-sm">
      <span className="font-medium text-emerald-900">{spec?.label || measure.id}</span>
      {(spec?.args || []).map((argKey) => (
        <select
          key={argKey}
          className="rounded border border-emerald-300 bg-white px-1 text-xs"
          value={measure[argKey] || ""}
          onChange={(e) => onChange({ ...measure, [argKey]: e.target.value })}
          data-testid={`measure-arg-${measure.id}-${argKey}`}
        >
          <option value="">{argKey}…</option>
          {argFields(measure.id).map((f) => (
            <option key={f.id} value={f.id}>{f.label}</option>
          ))}
        </select>
      ))}
      <button
        className="text-emerald-700 hover:text-red-600"
        onClick={onRemove}
        data-testid={`measure-remove-${measure.id}`}
      >
        ×
      </button>
    </div>
  );
}

// ── Value-with-tooltip helper for the Incomplete chip ──────────────────
function CellValue({ value, columnId }) {
  const isIncomplete = value === "Incomplete";
  if (!isIncomplete) return <span>{value == null ? "" : String(value)}</span>;
  return (
    <span
      className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-900"
      title={INCOMPLETE_TIP}
      data-testid="incomplete-caveat-chip"
    >
      Incomplete <span aria-hidden>ⓘ</span>
    </span>
  );
}

// ── Formatting per column (2026-08-21 polish + Defect 2) ───────────────
// Grain buckets arrive as plain strings from the engine: "2026-07",
// "2026-07-06" (Monday of ISO week), "2026". We format for the human
// eye — never with `new Date()` on a bare YYYY-MM-DD (see lib/dateOnly
// convention). Chart tooltips + axes share the same helper.
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function formatGrainLabel(value, grain) {
  if (value == null || value === "") return "";
  const s = String(value);
  if (grain === "month") {
    const m = s.match(/^(\d{4})-(\d{2})$/);
    if (m) return `${MONTHS_SHORT[parseInt(m[2], 10) - 1]} ${m[1]}`;
  }
  if (grain === "day") {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${MONTHS_SHORT[parseInt(m[2], 10) - 1]} ${parseInt(m[3], 10)}, ${m[1]}`;
  }
  if (grain === "week") {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `Wk of ${MONTHS_SHORT[parseInt(m[2], 10) - 1]} ${parseInt(m[3], 10)}, ${m[1]}`;
  }
  if (grain === "ytd") return s;  // "2026"
  return s;
}

// Format a measure value for display (table + chart tooltip). Export
// uses openpyxl number_formats on the backend — must stay in sync.
function formatMeasure(value, columnId) {
  if (value == null) return "";
  if (typeof value !== "number") return String(value);
  if (columnId === "pct_of_total" || columnId.startsWith("pct_reached")) {
    return `${value.toFixed(1)}%`;
  }
  if (columnId.startsWith("avg_days") || columnId.startsWith("median_days")) {
    return value.toFixed(1);
  }
  // count / count_distinct → integer.
  return Number.isInteger(value) ? String(value) : String(Math.round(value));
}

// Format any cell for the results table.
function formatCell(column, value) {
  if (column.kind === "date" && column.grain) return formatGrainLabel(value, column.grain);
  if (column.kind === "measure") return formatMeasure(value, column.id);
  return value == null ? "" : String(value);
}

// ── Main page ──────────────────────────────────────────────────────────
export default function Reports() {
  const [catalog, setCatalog] = useState([]);
  const [measures, setMeasures] = useState([]);
  const [grains, setGrains] = useState([]);
  const [limits, setLimits] = useState({});
  const [saved, setSaved] = useState([]);
  const [savedError, setSavedError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [staleFields, setStaleFields] = useState(null);
  const [loadedFromSaved, setLoadedFromSaved] = useState(null);

  // Builder state — defaults are hydrated from the catalog on first
  // load (nothing hardcoded here; the catalog drives every field id).
  const [dateBase, setDateBase] = useState("");
  const [datePreset, setDatePreset] = useState("last30");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [grain, setGrain] = useState("");
  const [filters, setFilters] = useState([]);
  const [addFilterField, setAddFilterField] = useState("");
  const [groupBy, setGroupBy] = useState([]);
  const [addGroupField, setAddGroupField] = useState("");
  const [pickedMeasures, setPickedMeasures] = useState([{ id: "count" }]);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  // Chart dataset — the FULL result set (up to the interactive cap), so
  // bucket totals never depend on the visible table page.
  const [chartData, setChartData] = useState(null);
  const [chartLines, setChartLines] = useState("");
  const [page, setPage] = useState(0);
  const perPage = 100;

  const currentUser = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null"); }
    catch { return null; }
  }, []);

  // ── Catalog load ─────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/reports/fields");
        const fields = data.fields || [];
        setCatalog(fields);
        setMeasures(data.measures || []);
        setGrains(data.grains || []);
        setLimits(data.limits || {});
        // Hydrate builder defaults from the catalog — first date-basis
        // field for dateBase, first advertised grain for grain.
        const firstBasis = fields.find((f) => f.is_date_base);
        if (firstBasis) setDateBase((cur) => cur || firstBasis.id);
        if ((data.grains || []).length) setGrain((cur) => cur || data.grains[0]);
      } catch (e) {
        toast.error("Failed to load report catalog");
      }
    })();
    refreshSaved();
     
  }, []);

  const refreshSaved = useCallback(async () => {
    try {
      const { data } = await api.get("/reports/saved");
      setSaved(data || []);
      setSavedError(null);
    } catch (e) {
      setSavedError("Failed to load saved reports");
    }
  }, []);

  // ── Derived catalog subsets ──────────────────────────────────────────
  const dateBaseFields = useMemo(
    () => catalog.filter((f) => f.is_date_base),
    [catalog],
  );
  const dimensionFields = useMemo(
    () => catalog.filter((f) => f.kind === "dimension"),
    [catalog],
  );
  const filterableFields = useMemo(
    () => catalog.filter((f) => f.filterable !== false),
    [catalog],
  );
  const activeDefinition = useMemo(() => {
    // `ytd` is a chart/bucketing convention, not an engine grain: the
    // engine's `ytd` truncates to YEAR (one bucket). The page requests
    // MONTH buckets over a Jan-1 → today range instead.
    const rng = grain === "ytd"
      ? rangeFor("ytd")
      : rangeFor(datePreset, customFrom, customTo);
    const body = {
      date_base: dateBase,
      date_grain: effectiveGrain(grain),
      date_from: rng.date_from,
      date_to: rng.date_to,
      filters,
      group_by: groupBy,
      measures: pickedMeasures,
    };
    // Client-side sort default kept minimal; server enforces its own.
    return body;
  }, [dateBase, datePreset, customFrom, customTo, grain, filters, groupBy, pickedMeasures]);

  // ── Run ──────────────────────────────────────────────────────────────
  // The chart aggregates by time bucket over the WHOLE result set. The
  // table is paginated (100/page), so a second /run at the interactive
  // cap feeds the chart — reusing the page response when it already
  // holds every row. Zero API changes; the table is untouched.
  const loadChartData = async (pageResponse) => {
    if (!grain || grain === "none") { setChartData(null); return; }
    if ((pageResponse.offset || 0) === 0 &&
        (pageResponse.row_count || 0) >= (pageResponse.total || 0)) {
      setChartData(pageResponse);
      return;
    }
    try {
      const cap = limits.max_interactive_rows || 10000;
      const { data } = await api.post("/reports/run", {
        ...activeDefinition, limit: cap, offset: 0,
      });
      setChartData(data);
    } catch (e) {
      setChartData(null);
      toast.error("Chart data load failed (table is unaffected)");
    }
  };

  const runReport = async () => {
    // Reversed dates would otherwise run and return zero rows silently.
    const rangeError = rangeValidationError(
      datePreset === "custom" ? customFrom : null,
      datePreset === "custom" ? customTo : null,
    );
    if (rangeError) { toast.error(rangeError); return; }
    setRunning(true);
    setStaleFields(null);
    try {
      const body = { ...activeDefinition, limit: perPage, offset: page * perPage };
      const { data } = await api.post("/reports/run", body);
      setResults(data);
      await loadChartData(data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.error === "saved_report_stale_fields") {
        setStaleFields(detail.stale_fields || []);
      } else if (status === 422) {
        toast.error(`Report validation failed: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
      } else {
        toast.error("Report run failed");
      }
    } finally {
      setRunning(false);
    }
  };

  // ── Export ───────────────────────────────────────────────────────────
  const exportXlsx = async () => {
    try {
      const body = { ...activeDefinition, report_name: loadedFromSaved?.name || "Ad-hoc report" };
      const res = await api.post("/reports/export", body, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/i);
      download(res.data, m?.[1] || `report_${Date.now()}.xlsx`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  // ── Save ─────────────────────────────────────────────────────────────
  const saveReport = async () => {
    const name = window.prompt("Report name?");
    if (!name) return;
    try {
      await api.post("/reports/saved", {
        name,
        is_shared: false,
        definition: activeDefinition,
      });
      toast.success(`Saved "${name}"`);
      refreshSaved();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  // ── Load a saved definition into the builder ────────────────────────
  const loadSaved = (row) => {
    const d = row.definition || {};
    const firstBasis = catalog.find((f) => f.is_date_base);
    setDateBase(d.date_base || firstBasis?.id || "");
    setGrain(d.date_grain || grains[0] || "");
    setFilters(d.filters || []);
    setGroupBy(d.group_by || []);
    // 2026-08-21 hotfix (Defect 1): do NOT inject the fresh-session
    // `count` default when loading a saved definition. Geoff's Lookup
    // template ships without measures on purpose — Brief §3.2 says
    // grain=none/absent + no group_by + no measures = raw row list.
    // Adding a default measure flips the engine into aggregate mode
    // and returns a single-row count instead of the row list.
    setPickedMeasures(d.measures || []);
    setDatePreset(d.date_from || d.date_to ? "custom" : "last30");
    setCustomFrom(d.date_from || "");
    // Stored definitions carry the ENGINE's exclusive end; the picker is
    // inclusive. −1 day here mirrors the +1 on submit, so a saved report
    // re-runs with a byte-identical range.
    setCustomTo(engineToPickerTo(d.date_to));
    setStaleFields(null);
    setLoadedFromSaved(row);
    setDrawerOpen(false);
    setPage(0);
    setResults(null);
    setChartData(null);
  };

  const deleteSaved = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await api.delete(`/reports/saved/${row.id}`);
      toast.success("Deleted");
      refreshSaved();
    } catch (e) {
      toast.error("Delete failed (only the owner can delete)");
    }
  };

  const forkSaved = async (row) => {
    const name = window.prompt("Name your copy?", `${row.name} (my copy)`);
    if (!name) return;
    try {
      await api.post("/reports/saved", {
        name,
        is_shared: false,
        definition: row.definition,
      });
      toast.success(`Forked "${row.name}" as "${name}"`);
      refreshSaved();
    } catch (e) {
      toast.error("Fork failed");
    }
  };

  // ── Inclusive range echo + validation ───────────────────────────────
  // Derived from the SAME translation the submitted range uses, so the
  // line can never disagree with what runs.
  const rangeInclusive = useMemo(() => {
    const inc = datePreset === "custom"
      ? { from: customFrom, to: customTo }
      : inclusiveRange(rangeFor(datePreset));
    return {
      ...inc,
      echo: rangeEcho(inc.from, inc.to),
      error: rangeValidationError(inc.from, inc.to),
    };
  }, [datePreset, customFrom, customTo]);

  // ── Range header text ────────────────────────────────────────────────
  const rangeLabel = useMemo(() => {
    const baseLabel = catalog.find((f) => f.id === dateBase)?.label || dateBase;
    const inc = datePreset === "custom"
      ? { from: customFrom, to: customTo }
      : inclusiveRange(rangeFor(datePreset));
    if (!inc.from && !inc.to) return baseLabel;
    return `${baseLabel} · ${inc.from || "…"} → ${inc.to || "…"}`;
  }, [datePreset, customFrom, customTo, dateBase, catalog]);

  // ── Chart: aggregate by TIME BUCKET, optionally split into one line
  // per value of a chosen group-by dimension. Independent of the
  // table's group-by detail (which is left untouched).
  const chartDimOptions = useMemo(
    () => groupBy
      .map((gid) => catalog.find((c) => c.id === gid))
      .filter(Boolean),
    [groupBy, catalog],
  );

  // Default to the first group-by dimension; fall back to None (total)
  // when the selected dimension is removed from group-by.
  useEffect(() => {
    setChartLines((cur) => {
      if (cur && groupBy.includes(cur)) return cur;
      return groupBy[0] || "";
    });
  }, [groupBy]);

  const chart = useMemo(() => {
    if (!chartData || !grain || grain === "none") return null;
    const cols = chartData.columns || [];
    const bucketCol = cols.find((c) => c.kind === "date" && c.grain);
    const measureCols = cols.filter((c) => c.kind === "measure");
    if (!bucketCol || !measureCols.length) return null;

    const rng = grain === "ytd"
      ? rangeFor("ytd")
      : rangeFor(datePreset, customFrom, customTo);
    const bucketKeys = bucketKeysForRange(bucketCol.grain, rng.date_from, rng.date_to);
    const dim = chartLines && groupBy.includes(chartLines) ? chartLines : null;

    const { data, series, truncated } = buildChartSeries({
      rows: chartData.rows || [],
      bucketField: bucketCol.id,
      measureFields: measureCols.map((c) => c.id),
      dimensionField: dim,
      bucketKeys,
    });
    const measureLabelById = Object.fromEntries(measureCols.map((c) => [c.id, c.label]));
    return {
      data,
      series,
      truncated,
      grain: bucketCol.grain,
      withYear: spansYears(data.map((d) => d.bucket)),
      total: grandTotal(data, series),
      splitLabel: dim ? (catalog.find((c) => c.id === dim)?.label || dim) : null,
      measureLabel: dim ? (measureCols[0]?.label || "") : "",
      legendName: (key) => (dim ? key : (measureLabelById[key] || key)),
      measureIdFor: (key) => (dim ? measureCols[0].id : key),
    };
  }, [chartData, grain, chartLines, groupBy, catalog, datePreset, customFrom, customTo]);

  const overCap =
    results?.total >= (limits.max_interactive_rows || 10000);

  return (
    <div className="p-6" data-testid="reports-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" data-testid="reports-title">
            Referral Reports
          </h1>
          <p className="text-sm text-slate-500">
            Runs against the R1 view · admin + sales
          </p>
        </div>
        <div className="flex gap-2">
          <button
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
            onClick={() => setDrawerOpen(true)}
            data-testid="open-saved-drawer"
          >
            Saved reports ({saved.length})
          </button>
          <button
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
            onClick={saveReport}
            data-testid="save-report-button"
          >
            Save report
          </button>
          <button
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
            onClick={exportXlsx}
            disabled={!results}
            title={overCap ? "Result is at the interactive row cap; export streams up to 100k rows" : ""}
            data-testid="export-xlsx-button"
          >
            Export to Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Left rail */}
        <aside className="col-span-3 space-y-4" data-testid="builder-rail">
          <section>
            <div className="mb-1 text-xs font-semibold uppercase text-slate-500">Date basis</div>
            <select
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              value={dateBase}
              onChange={(e) => setDateBase(e.target.value)}
              data-testid="date-basis-select"
            >
              {dateBaseFields.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </section>

          <section>
            <div className="mb-1 text-xs font-semibold uppercase text-slate-500">Date range</div>
            <select
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value)}
              data-testid="date-preset-select"
            >
              {DATE_PRESETS.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            {datePreset === "custom" && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <label className="flex flex-col gap-1 text-xs text-slate-500">
                  From (inclusive)
                  <input
                    type="date"
                    className="rounded border border-slate-300 px-2 py-1 text-xs"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    data-testid="date-from-input"
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-500">
                  To (inclusive)
                  <input
                    type="date"
                    className="rounded border border-slate-300 px-2 py-1 text-xs"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    data-testid="date-to-input"
                  />
                </label>
              </div>
            )}
            {/* Live echo — same translation as the submitted range. */}
            {rangeInclusive.error ? (
              <div className="mt-2 text-xs text-red-600" data-testid="date-range-error">
                {rangeInclusive.error}
              </div>
            ) : rangeInclusive.echo ? (
              <div className="mt-2 text-xs text-slate-500" data-testid="date-range-echo">
                {rangeInclusive.echo}
              </div>
            ) : null}
          </section>

          <section>
            <div className="mb-1 text-xs font-semibold uppercase text-slate-500">Grain</div>
            <select
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
              value={grain}
              onChange={(e) => setGrain(e.target.value)}
              data-testid="grain-select"
            >
              {grains.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            {grain === "ytd" && (
              <div className="mt-1 text-xs text-slate-500" data-testid="ytd-grain-note">
                YTD buckets by month, Jan 1 → today (overrides the range above)
              </div>
            )}
          </section>

          <section>
            <div className="mb-1 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase text-slate-500">Filters</div>
              <select
                className="text-xs"
                value={addFilterField}
                onChange={(e) => {
                  const fid = e.target.value;
                  if (!fid) return;
                  const f = catalog.find((c) => c.id === fid);
                  if (!f) return;
                  const ops = OPS_FOR_TYPE[f.type] || OPS_FOR_TYPE.string;
                  setFilters([...filters, { field: fid, op: ops[0], value: "" }]);
                  setAddFilterField("");
                }}
                data-testid="add-filter-select"
              >
                <option value="">+ add</option>
                {filterableFields.map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              {filters.map((filt, i) => {
                const f = catalog.find((c) => c.id === filt.field);
                if (!f) return null;
                return (
                  <FilterChip
                    key={`${filt.field}-${i}`}
                    field={f}
                    filter={filt}
                    onChange={(next) => {
                      const copy = filters.slice();
                      copy[i] = next;
                      setFilters(copy);
                    }}
                    onRemove={() => setFilters(filters.filter((_, j) => j !== i))}
                  />
                );
              })}
            </div>
          </section>

          <section>
            <div className="mb-1 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase text-slate-500">Group by</div>
              <select
                className="text-xs"
                value={addGroupField}
                onChange={(e) => {
                  const fid = e.target.value;
                  if (!fid || groupBy.includes(fid)) return;
                  setGroupBy([...groupBy, fid]);
                  setAddGroupField("");
                }}
                data-testid="add-group-select"
              >
                <option value="">+ add</option>
                {dimensionFields.map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              {groupBy.map((gid) => {
                const f = catalog.find((c) => c.id === gid);
                return (
                  <span
                    key={gid}
                    className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-1 text-xs text-indigo-900"
                    data-testid={`group-chip-${gid}`}
                  >
                    {f?.label || gid}
                    <button
                      onClick={() => setGroupBy(groupBy.filter((g) => g !== gid))}
                    >×</button>
                  </span>
                );
              })}
            </div>
          </section>

          <section>
            <div className="mb-1 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase text-slate-500">Measures</div>
              <select
                className="text-xs"
                value=""
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) return;
                  setPickedMeasures([...pickedMeasures, { id }]);
                }}
                data-testid="add-measure-select"
              >
                <option value="">+ add</option>
                {measures.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              {pickedMeasures.map((m, i) => (
                <MeasureChip
                  key={`${m.id}-${i}`}
                  measure={m}
                  catalog={catalog}
                  measures={measures}
                  onChange={(next) => {
                    const copy = pickedMeasures.slice();
                    copy[i] = next;
                    setPickedMeasures(copy);
                  }}
                  onRemove={() => setPickedMeasures(pickedMeasures.filter((_, j) => j !== i))}
                />
              ))}
            </div>
          </section>

          <button
            className="w-full rounded bg-slate-900 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
            onClick={() => { setPage(0); runReport(); }}
            disabled={running}
            data-testid="run-report-button"
          >
            {running ? "Running…" : "Run report"}
          </button>
        </aside>

        {/* Results */}
        <main className="col-span-9">
          {staleFields && (
            <div
              className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="stale-fields-banner"
            >
              <div className="font-semibold">Saved report is out of date</div>
              <div>
                Fields no longer in the catalog: {staleFields.join(", ")}
              </div>
              {loadedFromSaved && (
                <button
                  className="mt-2 rounded border border-amber-500 bg-white px-2 py-0.5 text-xs"
                  onClick={() => forkSaved(loadedFromSaved)}
                  data-testid="fork-stale-button"
                >
                  Save as my copy (to edit)
                </button>
              )}
            </div>
          )}

          {results ? (
            <>
              <div
                className="mb-2 flex items-center justify-between rounded border border-slate-200 bg-white px-3 py-2 text-sm"
                data-testid="result-header"
              >
                <div>
                  <span className="font-medium">{results.total}</span>{" "}
                  <span data-testid="result-count-noun">
                    {resultCountLabel(results.total, isGroupedResult(results.columns))}
                  </span>
                  {" · "}{rangeLabel}
                </div>
                {intakeRowsNote(results.intake_row_count) && (
                  <div className="text-xs text-slate-500" data-testid="intake-rows-note">
                    {intakeRowsNote(results.intake_row_count)}
                  </div>
                )}
                <div className="text-xs text-slate-500">
                  page {page + 1} · {results.row_count} of {results.total}
                </div>
              </div>

              {chart && chart.data.length > 0 && (
                <div className="mb-4 rounded border border-slate-200 bg-white p-3" data-testid="chart-container">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold uppercase text-slate-500" htmlFor="chart-lines">
                        Chart lines
                      </label>
                      <select
                        id="chart-lines"
                        className="rounded border border-slate-300 px-2 py-1 text-xs"
                        value={chartLines}
                        onChange={(e) => setChartLines(e.target.value)}
                        data-testid="chart-lines-select"
                      >
                        <option value="">None (total)</option>
                        {chartDimOptions.map((f) => (
                          <option key={f.id} value={f.id}>{f.label}</option>
                        ))}
                      </select>
                      {chart.splitLabel && (
                        <span className="text-xs text-slate-500" data-testid="chart-split-note">
                          {chart.measureLabel} by {chart.splitLabel}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500" data-testid="chart-total">
                      {chart.data.length} {chart.grain} bucket{chart.data.length === 1 ? "" : "s"}
                      {" · total "}
                      {formatMeasure(chart.total, chart.measureIdFor(chart.series[0]))}
                      {chart.truncated && " · top 7 + Other"}
                    </div>
                  </div>
                  <ReportTimeChart chart={chart} formatValue={formatMeasure} />
                </div>
              )}

              <div className="overflow-x-auto rounded border border-slate-200 bg-white">
                <table className="w-full text-sm" data-testid="results-table">
                  <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                    <tr>
                      {results.columns.map((c) => (
                        <th key={c.id} className="px-3 py-2">{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.rows.map((row, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {results.columns.map((c) => {
                          const raw = row[c.id];
                          // Incomplete caveat chip still fires on the
                          // dimension value; measures + grain buckets
                          // format via `formatCell` so % gets 1-decimal
                          // and grain labels render as "Jul 2026" etc.
                          if (raw === "Incomplete") {
                            return (
                              <td key={c.id} className="px-3 py-2">
                                <CellValue value={raw} columnId={c.id} />
                              </td>
                            );
                          }
                          return (
                            <td key={c.id} className="px-3 py-2" data-testid={`cell-${c.id}`}>
                              {formatCell(c, raw)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                <button
                  className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
                  disabled={page === 0 || running}
                  onClick={() => { setPage(page - 1); runReport(); }}
                  data-testid="page-prev"
                >
                  ← Prev
                </button>
                <div>
                  Dates bucketed in America/Los_Angeles · ISO weeks start Monday · YTD from Jan 1
                </div>
                <button
                  className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
                  disabled={(page + 1) * perPage >= (results.total || 0) || running}
                  onClick={() => { setPage(page + 1); runReport(); }}
                  data-testid="page-next"
                >
                  Next →
                </button>
              </div>
            </>
          ) : (
            <div className="rounded border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500">
              Pick a date basis, range, grain, and measures — then Run.
              <div className="mt-3 text-xs">
                Dates bucketed in America/Los_Angeles · ISO weeks start Monday · YTD from Jan 1
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Saved-reports drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40"
          onClick={() => setDrawerOpen(false)}
          data-testid="saved-drawer"
        >
          <div
            className="absolute right-0 top-0 h-full w-96 bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="font-semibold">Saved reports</div>
              <button onClick={() => setDrawerOpen(false)}>×</button>
            </div>
            {savedError ? (
              <div className="text-sm text-red-600">{savedError}</div>
            ) : (
              <ul className="space-y-2">
                {saved.map((row) => {
                  const isMine = row.owner_username === currentUser?.username;
                  const isTemplate = row.owner_username === "system";
                  return (
                    <li
                      key={row.id}
                      className="rounded border border-slate-200 p-2 text-sm"
                      data-testid={`saved-row-${row.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="mr-2 flex-1">
                          <div className="font-medium">{row.name}</div>
                          <div className="text-xs text-slate-500">
                            {row.is_shared && (
                              <span
                                className="mr-1 rounded bg-indigo-100 px-1 py-0.5 text-indigo-800"
                                data-testid="shared-badge"
                              >
                                {isTemplate ? "Template" : "Shared"}
                              </span>
                            )}
                            by {row.owner_username}
                            {row.last_edited_by
                              && row.last_edited_by !== row.owner_username && (
                              <span data-testid={`last-edited-${row.id}`}>
                                {" · last edited by "}{row.last_edited_by}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <button
                            className="rounded bg-slate-900 px-2 py-0.5 text-xs text-white"
                            onClick={() => loadSaved(row)}
                            data-testid={`load-saved-${row.id}`}
                          >
                            Load
                          </button>
                          {!isMine && (
                            <button
                              className="rounded border border-slate-300 px-2 py-0.5 text-xs"
                              onClick={() => forkSaved(row)}
                              data-testid={`fork-saved-${row.id}`}
                            >
                              Save as my copy
                            </button>
                          )}
                          {(isMine || (currentUser?.role === "admin" && !isTemplate)) && (
                            <button
                              className="rounded border border-red-300 px-2 py-0.5 text-xs text-red-700"
                              onClick={() => deleteSaved(row)}
                              data-testid={`delete-saved-${row.id}`}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
